import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import { useNavigate } from "react-router-dom";
import {
  getTrayTone,
  getTrayToneClasses,
  resolveNotificationCategory,
  resolveNotificationKind,
  type TrayNotificationData,
} from "@/shared/utils/notificationTray";

interface NotificationAlertProps {
  title: string;
  body: string;
  isVisible: boolean;
  onClose: () => void;
  data?: TrayNotificationData | Record<string, unknown>;
  /** No auto-dismiss; primary action does not navigate or close (for UI preview). */
  isPersistent?: boolean;
}

export function NotificationAlert({
  title,
  body,
  isVisible,
  onClose,
  data,
  isPersistent = false,
}: NotificationAlertProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();

  const tray = data as TrayNotificationData | undefined;
  const displayTitle = String(tray?.title ?? title ?? "").trim() || "Notification";
  const displayBody = String(tray?.body ?? body ?? "").trim();
  const category = resolveNotificationCategory(tray);
  const kind = resolveNotificationKind(tray);
  const tone = getTrayTone(kind);
  const toneClasses = getTrayToneClasses(tone);

  const primaryLabel =
    category === "patrol" ? "Scan QR code" : category === "shift" ? "View shifts" : null;

  const handlePrimary = () => {
    if (isPersistent) {
      return;
    }
    if (category === "patrol") {
      navigate("/shifts", { state: { openPatrolScanner: true } });
    } else if (category === "shift") {
      navigate("/shifts");
    }
    onClose();
  };

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
    } else {
      setIsAnimating(false);
    }

    if (isVisible && !isPersistent) {
      const timer = setTimeout(() => {
        onClose();
      }, 12_000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isPersistent, onClose]);

  if (!isVisible) return null;

  const truncatedBody =
    displayBody.length > 160 ? `${displayBody.substring(0, 160)}…` : displayBody;

  return (
    <div
      className={cn(
        "fixed inset-x-0 top-0 z-[9999] flex justify-center px-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-4 pointer-events-none"
      )}
      style={{ zIndex: 9999 }}
    >
      <div
        className={cn(
          "pointer-events-auto w-full max-w-lg transform transition duration-300 ease-out",
          isAnimating ? "translate-y-0 opacity-100" : "-translate-y-3 opacity-0"
        )}
      >
        <div
          className={cn(
            "relative overflow-hidden rounded-xl border px-4 pb-4 pt-3",
            toneClasses.card
          )}
        >
          <div className="absolute right-2 top-2">
            <button
              type="button"
              onClick={onClose}
              aria-label="Dismiss notification"
              className={toneClasses.close}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="pr-10">
            <h4 className={cn("text-sm font-semibold leading-snug", toneClasses.title)}>
              {displayTitle}
            </h4>
            {truncatedBody ? (
              <p className={cn("mt-1.5 text-xs leading-relaxed", toneClasses.body)}>
                {truncatedBody}
              </p>
            ) : null}
            {primaryLabel ? (
              <button
                type="button"
                onClick={handlePrimary}
                className={cn("mt-3 block text-left text-xs", toneClasses.action)}
              >
                {primaryLabel}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
